import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, CircularProgress } from "@mui/material";
import { Link } from "react-router-dom";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import { getMyBooks } from "services/books";
import "./publications.css";
const currency = (value) =>
  value == null || value === ""
    ? "Não informado"
    : new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(
        Number(String(value).replace(",", ".")) || 0
      );
const searchKey = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase("pt-BR");
const dimension = (value) =>
  Number(value)
    ? new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 2 }).format(Number(value))
    : null;
function BookCover({ book }) {
  const [failedSource, setFailedSource] = useState(null);
  const source = book.coverThumbnailUrl || book.coverImageUrl;
  return (
    <div className="publication-card__cover">
      {source && failedSource !== source ? (
        <img
          src={source}
          alt={`Capa de ${book.title}`}
          loading="lazy"
          decoding="async"
          referrerPolicy="no-referrer"
          onError={() => setFailedSource(source)}
        />
      ) : (
        <div className="publication-card__placeholder">
          <MenuBookOutlinedIcon />
          <span>Capa indisponível</span>
        </div>
      )}
    </div>
  );
}
function PublicationCard({ book }) {
  const pending = book.trayStatus === "sync_pending";
  const format = [dimension(book.widthCm), dimension(book.heightCm)].filter(Boolean).join(" × ");
  return (
    <article className="publication-card" aria-label={book.title}>
      <div className="publication-card__main">
        <BookCover book={book} />
        <div className="publication-card__identity">
          <span
            className={`publication-card__status ${
              pending || !book.trayProductId ? "is-pending" : ""
            }`}
          >
            <i aria-hidden="true" />
            {pending
              ? "Sincronização pendente"
              : book.trayProductId
              ? "Vinculado à loja"
              : "Sem vínculo com a loja"}
          </span>
          <h2 title={book.title}>{book.title || "Publicação sem título"}</h2>
          {book.subtitle && <p className="publication-card__subtitle">{book.subtitle}</p>}
          <p className="publication-card__author">{book.author || "Autor não informado"}</p>
          <div className="publication-card__specs">
            {book.pages ? <span>{book.pages} páginas</span> : null}
            {format && <span>{format} cm</span>}
          </div>
        </div>
      </div>
      <dl className="publication-card__codes">
        <div>
          <dt>Código PSO / referência</dt>
          <dd>{book.internalCode || book.reference || book.bookId || "Não informado"}</dd>
        </div>
        <div>
          <dt>ISBN</dt>
          <dd>{book.isbn || "Sem ISBN"}</dd>
        </div>
      </dl>
      <footer className="publication-card__footer">
        <div>
          <span>Preço do livro</span>
          <strong>{currency(book.price)}</strong>
        </div>
        <Link
          to={`/my-publications/${book.id}/edit`}
          className="publication-card__edit"
          aria-label={`Editar ${book.title}`}
        >
          <EditOutlinedIcon />
          Editar livro
        </Link>
      </footer>
    </article>
  );
}
function MyPublications({ user }) {
  const [books, setBooks] = useState([]),
    [search, setSearch] = useState(""),
    [filter, setFilter] = useState("all"),
    [sort, setSort] = useState("recent"),
    [error, setError] = useState(""),
    [loading, setLoading] = useState(true),
    [loaded, setLoaded] = useState(false);
  const generation = useRef(0);
  const load = useCallback(async () => {
    const current = ++generation.current;
    if (!user?.uid) return;
    setLoading(true);
    setError("");
    try {
      const result = await getMyBooks({ authId: user.uid });
      if (current === generation.current) {
        setBooks(result.books || []);
        setLoaded(true);
      }
    } catch (failure) {
      if (current === generation.current)
        setError(failure.message || "Não foi possível carregar suas publicações.");
    } finally {
      if (current === generation.current) setLoading(false);
    }
  }, [user?.uid]);
  useEffect(() => {
    setBooks([]);
    setLoaded(false);
    setSearch("");
    setFilter("all");
    load();
    const refresh = () => load();
    window.addEventListener("focus", refresh);
    const timer = setInterval(() => {
      if (document.visibilityState === "visible") load();
    }, 10 * 60 * 1000);
    return () => {
      generation.current++;
      window.removeEventListener("focus", refresh);
      clearInterval(timer);
    };
  }, [load]);
  const linked = books.filter((book) => book.trayProductId).length;
  const visible = useMemo(() => {
    const result = books.filter(
      (book) =>
        (filter === "all" || (filter === "linked" ? !!book.trayProductId : !book.trayProductId)) &&
        [book.title, book.subtitle, book.author, book.internalCode, book.reference, book.isbn].some(
          (value) => searchKey(value).includes(searchKey(search.trim()))
        )
    );
    if (sort === "title")
      result.sort((a, b) => String(a.title || "").localeCompare(String(b.title || ""), "pt-BR"));
    return result;
  }, [books, filter, search, sort]);
  const reset = () => {
    setSearch("");
    setFilter("all");
  };
  return (
    <DashboardLayout>
      <main className="publications-page">
        <header className="publications-page__header">
          <div>
            <span className="publications-page__eyebrow">SUA ESTANTE</span>
            <h1>Minhas publicações</h1>
            <p>Cada livro tem uma história. Encontre e cuide das suas obras por aqui.</p>
          </div>
          <Link to="/publish-here" className="publications-page__publish">
            <AddRoundedIcon />
            Publicar um livro
          </Link>
        </header>
        {error && (
          <Alert severity="error">
            {error}
            {loaded && " Suas últimas publicações carregadas continuam disponíveis."}
          </Alert>
        )}
        <section className="publications-page__toolbar" aria-label="Buscar e filtrar publicações">
          <div className="publications-page__search">
            <SearchRoundedIcon aria-hidden="true" />
            <input
              aria-label="Buscar publicações por título, autor, código PSO ou ISBN"
              placeholder="Buscar por título, autor, código PSO ou ISBN"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            {search && (
              <button type="button" aria-label="Limpar busca" onClick={() => setSearch("")}>
                <CloseRoundedIcon />
              </button>
            )}
          </div>
          <div className="publications-page__sort">
            <label htmlFor="publication-sort">Ordenar</label>
            <select
              id="publication-sort"
              value={sort}
              onChange={(event) => setSort(event.target.value)}
            >
              <option value="recent">Mais recentes</option>
              <option value="title">Título: A–Z</option>
            </select>
          </div>
          <button
            type="button"
            className="publications-page__refresh"
            onClick={load}
            disabled={loading}
            aria-label="Atualizar publicações"
          >
            <RefreshRoundedIcon />
          </button>
        </section>
        {loaded && (
          <div className="publications-page__filterbar">
            <div
              className="publications-page__filters"
              role="group"
              aria-label="Vínculo com a loja"
            >
              {[
                ["all", "Todas", books.length],
                ["linked", "Na loja", linked],
                ["unlinked", "Sem vínculo", books.length - linked],
              ].map(([value, label, total]) => (
                <button
                  type="button"
                  key={value}
                  aria-pressed={filter === value}
                  onClick={() => setFilter(value)}
                >
                  {label}
                  <span>{total}</span>
                </button>
              ))}
            </div>
            <span className="publications-page__result" role="status">
              {loading
                ? "Atualizando…"
                : `${visible.length} ${visible.length === 1 ? "publicação" : "publicações"}`}
            </span>
          </div>
        )}
        {!loaded && loading && (
          <div className="publications-page__loading" role="status">
            <CircularProgress size={25} />
            Preparando sua estante…
          </div>
        )}
        {!loaded && !loading && error && (
          <button className="publications-page__retry" onClick={load}>
            Tentar novamente
          </button>
        )}
        {loaded &&
          (visible.length ? (
            <section className="publications-page__grid" aria-label="Suas publicações">
              {visible.map((book) => (
                <PublicationCard key={book.id || book.bookId} book={book} />
              ))}
            </section>
          ) : (
            <section className="publications-page__empty">
              <MenuBookOutlinedIcon />
              <h2>
                {books.length ? "Nenhuma obra encontrada" : "Sua próxima história começa aqui"}
              </h2>
              <p>
                {books.length
                  ? "Tente outro título, autor ou código, ou limpe os filtros."
                  : "Publique seu primeiro livro para vê-lo na sua estante."}
              </p>
              {books.length ? (
                <button onClick={reset}>Limpar busca e filtros</button>
              ) : (
                <Link to="/publish-here">Publicar meu primeiro livro</Link>
              )}
            </section>
          ))}
      </main>
    </DashboardLayout>
  );
}
export default MyPublications;
